/* eslint-disable no-underscore-dangle */
import { useEffect, useState, useRef } from 'react';
import { func, oneOf, shape, string } from 'prop-types';
import _ from 'lodash';
import TextField from '@material-ui/core/TextField';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';

import useEffectSkipFirst from '../../../utilities/UseEffectSkipFirst';
import { isNewReport } from '../../../services/ReportService';
import RecurrenceOptions, { recurrenceOptionsType, defaultRecurrenceOptions } from './RecurrenceOptions';
import EmailOptions, { emailOptionsType, defaultEmailOptions } from './EmailOptions';
import PdfOptions, { pdfOptionsType, defaultPdfOptions } from './PdfOptions';
import FilterOptions, { filtersOptionsType, defaultFiltersOptions } from './FilterOptions';
import NgpSmtpGuard from '../../form/NgpSMTPGuard';
import { getLogger } from '../../../utilities/UseLog';

const log = getLogger('ReportOptions');

export const MODES = {
    DOWNLOAD: 'download',
    EMAIL: 'email',
    SCHEDULE: 'schedule',
};

export const MODE_LABELS = {
    [MODES.DOWNLOAD]: t`Download`,
    [MODES.EMAIL]: t`Email`,
    [MODES.SCHEDULE]: t`Schedule`,
};

const pageOptionsType = shape({
    pageId: string,
});

export const optionsType = shape({
    name: string,
    user: string,
    page: pageOptionsType,
    pdf: pdfOptionsType,
    email: emailOptionsType,
    recurrence: recurrenceOptionsType,
    filters: filtersOptionsType,
});

export const defaultOptions = {
    name: '',
    page: {},
    pdf: defaultPdfOptions,
    email: defaultEmailOptions,
    recurrence: defaultRecurrenceOptions,
    filters: defaultFiltersOptions,
};

const smtpSetupMessage = i18n._(t`An SMTP server must be configured in order to email or schedule PDF reports`);

export default function ReportOptions({ mode, options, onChange, onValidate }) {
    const [name, setName] = useState(options.name);
    const [pageOptions] = useState(options.page);
    const [pdfOptions, setPdfOptions] = useState(options.pdf);
    const [emailOptions, setEmailOptions] = useState(options.email);
    const [recurrenceOptions, setRecurrenceOptions] = useState(options.recurrence);
    const [filtersOptions, setFiltersOptions] = useState(options.filters);
    const [nameValid, setNameValid] = useState(mode !== MODES.SCHEDULE);
    const [nameErrorMessage, setNameErrorMessage] = useState(undefined);
    const [pdfOptionsValid, setPdfOptionsValid] = useState(false);
    const [emailOptionsValid, setEmailOptionsValid] = useState(mode !== MODES.EMAIL);
    const [recurrenceOptionsValid, setRecurrenceOptionsValid] = useState(mode !== MODES.SCHEDULE);
    const [filtersOptionsValid, setFiltersOptionsValid] = useState(mode !== MODES.SCHEDULE);

    const onChangeCallback = useRef();
    const onValidateCallback = useRef();
    onChangeCallback.current = onChange;
    onValidateCallback.current = onValidate;

    useEffect(() => {
        onChangeCallback.current({
            name,
            page: pageOptions,
            pdf: pdfOptions,
            email: emailOptions,
            recurrence: recurrenceOptions,
            filters: filtersOptions,
        });
    }, [name, pageOptions, pdfOptions, emailOptions, recurrenceOptions, filtersOptions]);

    // Skip the first change, since it is always undefined.  This ensures that the
    // field is not marked invalid until it is changed or blurred.
    useEffectSkipFirst(
        () => {
            const valid = name && !_.isEmpty(name);
            setNameValid(valid);
            setNameErrorMessage(valid ? undefined : i18n._(t`A report name is required`));
        },
        [name],
        isNewReport(options) && !name
    );

    useEffect(() => {
        log.debugTable({
            nameValid,
            pdfOptionsValid,
            emailOptionsValid,
            recurrenceOptionsValid,
        });

        onValidateCallback.current({
            all: nameValid && pdfOptionsValid && emailOptionsValid && recurrenceOptionsValid,
            pdf: pdfOptionsValid,
        });
    }, [nameValid, pdfOptionsValid, emailOptionsValid, recurrenceOptionsValid, filtersOptionsValid]);

    function handleNameChange(event) {
        const { value: newName } = event.target;
        setName(newName);
    }

    return (
        <>
            {mode === MODES.SCHEDULE && (
                <NgpSmtpGuard message={smtpSetupMessage}>
                    <TextField
                        id="report-name"
                        label={<Trans>Report Name</Trans>}
                        value={name}
                        required
                        fullWidth
                        error={!!nameErrorMessage}
                        helperText={nameErrorMessage}
                        InputProps={{ autoFocus: true, inputProps: { onBlur: handleNameChange } }}
                        onChange={handleNameChange}
                    />
                    <RecurrenceOptions
                        options={options.recurrence}
                        isNew={isNewReport(options)}
                        onChange={setRecurrenceOptions}
                        onValidate={setRecurrenceOptionsValid}
                    />
                    <EmailOptions
                        options={options.email}
                        isNew={isNewReport(options)}
                        onChange={setEmailOptions}
                        onValidate={setEmailOptionsValid}
                    />
                    <PdfOptions
                        options={options.pdf}
                        requireTableRows
                        maxTableRows={1000}
                        onChange={setPdfOptions}
                        onValidate={setPdfOptionsValid}
                    />
                    <FilterOptions
                        options={options.filters}
                        recurrenceInterval={recurrenceOptions?.interval}
                        onChange={setFiltersOptions}
                        onValidate={setFiltersOptionsValid}
                    />
                </NgpSmtpGuard>
            )}
            {mode === MODES.EMAIL && (
                <NgpSmtpGuard message={smtpSetupMessage}>
                    <EmailOptions
                        options={options.email}
                        autoFocus
                        onChange={setEmailOptions}
                        onValidate={setEmailOptionsValid}
                    />
                    <PdfOptions
                        options={options.pdf}
                        maxTableRows={1000}
                        onChange={setPdfOptions}
                        onValidate={setPdfOptionsValid}
                    />
                </NgpSmtpGuard>
            )}
            {mode === MODES.DOWNLOAD && (
                <PdfOptions options={options.pdf} onChange={setPdfOptions} onValidate={setPdfOptionsValid} />
            )}
        </>
    );
}

ReportOptions.propTypes = {
    mode: oneOf(Object.values(MODES)).isRequired,
    options: optionsType.isRequired,
    onChange: func.isRequired,
    onValidate: func.isRequired,
};
